import { Checkbox, Form, Input, Modal, Select, Tooltip } from "antd";
import { useEffect, useState } from "react";
import {
  STATUS,
  USER_ACCOUNT_STATUS,
  VALIDATION_ERRORS,
} from "../../../utils/constants/common";
import { FormLineItemWrapper } from "../../UserDetail/UserInfoStyled";
import DynamicColorSelectWrapper from "./DynamicColorSelectWrapper";
import { userStatusOptions } from "../../UserDetail/UserDetailsData";
import { useAppDispatch } from "../../../hooks/reduxHooks";
import { GET, PUT } from "../../../service/api";
import { ApiUrl } from "../../../utils/constants/ApiUrl";
import { triggerToastMessage } from "../../../utils/utils";
import { IUserDetails } from "../../../interface/UserDetail";
import { RESPONSE_MESSAGES } from "../../../utils/constants/messages";
import { useManufacturingFileTypes } from "../../../hooks/useGetTestDataFileTypes";
import Loader from "../../../components/Loader/Loader";
import AccountNumberField from "../AccountNumberField/AccountNumberField";

const EditCustomerInfoModal = ({
  isOpen,
  closeModal,
  data,
}: {
  isOpen: boolean;
  closeModal: Function;
  data: IUserDetails;
}) => {
  const DEFAULT_SELECTED: string = "Active";

  const [isTestDataDisabled, setIsTestDataDisabled] = useState(
    !data.customer_id,
  );
  const isNodesEmpty = Object.values(data?.nodes || {})?.every(
    (node) => node.length === 0,
  );
  const dispatch = useAppDispatch();
  const { testDataFileTypes, testDataFileTypesLoading } =
    useManufacturingFileTypes();

  const [editCustomerForm] = Form.useForm();
  const [btnStatus, setBtnStatus] = useState(true);
  const [userAsSuperAdmin, setUserAsSuperAdmin] = useState(data.is_super_admin);
  const [selectedValue, setSelectedValue] = useState(
    data.status || DEFAULT_SELECTED,
  );

  const getFormInitialvalues = () => {
    return {
      name: data.name,
      email: data.email,
      status: data.status,
      accountNumber: data.customer_id ? data.customer_id.toString() : "",
      testData: getInitialTestData() || [],
    };
  };

  // const accountNumber = Form.useWatch("accountNumber", editCustomerForm);

  useEffect(() => {
    setUserAsSuperAdmin(data.is_super_admin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectChange = (value: string) => {
    setUserAsSuperAdmin(data.is_super_admin);
    setSelectedValue(value);
    handleFormChange();
  };

  const handleResponse = (payload: any) => {
    let subMessage = constructToastMsg(payload);
    triggerToastMessage(
      RESPONSE_MESSAGES.EDIT_USER.message,
      STATUS.SUCCESS,
      subMessage,
    );
  };

  const getInitialTestData = () => {
    return data?.manufacturing_file_types?.map((type) => type.id);
  };

  const constructToastMsg = (payload: any) => {
    const changed = Object.keys(data).filter(
      (key: string) =>
        key in payload &&
        payload[key]?.toString() !== data[key as keyof typeof data]?.toString(),
    );

    let msg = "";
    changed
      .map((key: string) => (key === "is_super_admin" ? "role" : key))
      .forEach((key: string, ind: number) => {
        msg =
          ind === changed.length - 1
            ? (ind === 0 ? `${key} ` : `${msg} and ${key}`) +
              " has been updated"
            : ind !== 0
              ? `${key}, ${msg}`
              : `${key}`;
      });
    msg = msg?.charAt(0).toUpperCase() + msg.slice(1);
    return msg;
  };

  const editUser = () => {
    setBtnStatus(true);
    editCustomerForm
      .validateFields()
      .then((values) => {
        const isStatusInvited =
          selectedValue.toLowerCase() === USER_ACCOUNT_STATUS.INVITED;
        // If we get here, the form is valid (including testData)
        const payload = {
          ...(isStatusInvited ? {} : { status: selectedValue }),
          is_super_admin: userAsSuperAdmin,
          name: values.name,
          ...(values?.testData?.length
            ? { manufacturing_file_types: values.testData }
            : { manufacturingFileTypes: null }),
          ...(values?.accountNumber
            ? { customer_id: values.accountNumber }
            : { customer_id: null }),
        };

        updateCustomer(payload);
      })
      .catch((errorInfo) => {
        // If validation fails, this block runs.
        // Ant Design AUTOMATICALLY shows the red error messages in the UI now.
        console.log("Validation Failed:", errorInfo);
      });
  };

  const updateCustomer = (payload: any) => {
    dispatch(
      PUT("customer/update", `${ApiUrl.customerList}${data.id}/`, payload)(),
    ).then((res) => {
      if (res?.payload?.status === STATUS.SUCCESS) {
        handleResponse(payload);
        dispatch(GET("customer/detail", `${ApiUrl.customerList}${data.id}/`)());
        reset();
      }
    });
  };

  const reset = () => {
    closeModal();
    setIsTestDataDisabled(!data.customer_id || isNodesEmpty);
    setUserAsSuperAdmin(data.is_super_admin);
    setSelectedValue(data.status);
    editCustomerForm.resetFields();
    setBtnStatus(true);
  };

  const handleFormChange = () => {
    //logic to disable the test data check boxes
    const accountErrors = editCustomerForm.getFieldError("accountNumber");
    const accountValue = editCustomerForm.getFieldValue("accountNumber");

    // LOGIC: To be enabled, we need a Value AND No Errors.
    // This covers your "Untouched" case because initially accountValue is "" (falsy)
    const isAccountValid = !!accountValue && accountErrors.length === 0;

    setIsTestDataDisabled(!isAccountValid || isNodesEmpty);
    const hasErrors = editCustomerForm
      .getFieldsError()
      .some(({ errors }) => errors.length);
    setBtnStatus(hasErrors);
  };

  const triggerValidationOnBlur = (field: string) => {
    const val = editCustomerForm.getFieldValue(field);
    editCustomerForm.setFieldsValue({ [field]: val?.trim() });
    editCustomerForm.validateFields([field]);
  };

  useEffect(() => {
    if (isOpen) {
      editCustomerForm.setFieldsValue(getFormInitialvalues());
      const isNodesEmpty = Object.values(data?.nodes || {})?.every(
        (node) => node.length === 0,
      );
      setIsTestDataDisabled(!data.customer_id || isNodesEmpty);
    }
  }, [isOpen]);

  return (
    <>
      <Modal
        destroyOnClose
        className="modal-title"
        title={`Edit Customer Info`}
        open={isOpen}
        okText="Update"
        okButtonProps={{ size: "large", disabled: btnStatus }}
        cancelButtonProps={{ size: "large", type: "text" }}
        onOk={editUser}
        onCancel={reset}
        closable={false}
      >
        {testDataFileTypesLoading ? (
          <div style={{ height: "268px" }}>
            <Loader />
          </div>
        ) : (
          <Form
            onFieldsChange={handleFormChange}
            autoComplete="off"
            size="large"
            form={editCustomerForm}
            layout="vertical"
            name="editCustomerForm"
            requiredMark={false}
            initialValues={getFormInitialvalues()}
          >
            <FormLineItemWrapper>
              <Form.Item
                style={{ flexGrow: 1 }}
                name="name"
                label="Customer Name"
                rules={[
                  {
                    required: true,
                    message: VALIDATION_ERRORS.name,
                    validateTrigger: "onBlur",
                  },
                  {
                    min: 3,
                    message: `Customer name should be atleast 3 characters`,
                    validateTrigger: "onBlur",
                  },
                ]}
              >
                <Input
                  autoComplete="edit-name"
                  value={data?.name}
                  placeholder="Customer Name"
                  onBlur={() => triggerValidationOnBlur("name")}
                  minLength={3}
                />
              </Form.Item>
              <DynamicColorSelectWrapper type={selectedValue}>
                <Form.Item name="status" label="Status">
                  <Tooltip placement="top" title="Change Status">
                    <Select
                      disabled={
                        selectedValue.toLowerCase() ===
                        USER_ACCOUNT_STATUS.INVITED
                      }
                      className="dynamic-color-select"
                      value={selectedValue}
                      onChange={handleSelectChange}
                      defaultValue={DEFAULT_SELECTED}
                      style={{ width: "12rem", height: "4.8rem" }}
                      options={userStatusOptions}
                    />
                  </Tooltip>
                </Form.Item>
              </DynamicColorSelectWrapper>
            </FormLineItemWrapper>
            <FormLineItemWrapper>
              <AccountNumberField onBlur={triggerValidationOnBlur} />
            </FormLineItemWrapper>
            <FormLineItemWrapper>
              <Form.Item
                name="testData"
                dependencies={["accountNumber"]}
                label={
                  <div
                    className={`${isTestDataDisabled ? "disabled-label" : ""}`}
                  >
                    Test Data Available
                  </div>
                }
                rules={[
                  ({ getFieldValue, getFieldError }) => ({
                    validator(_, value) {
                      if (!testDataFileTypes || isNodesEmpty) {
                        return Promise.resolve();
                      }

                      const accountNumber = getFieldValue("accountNumber");
                      const accountErrors =
                        editCustomerForm.getFieldError("accountNumber");

                      // Definition of "Valid": Field has a value AND has no validation errors
                      const isAccountValid =
                        // accountNumber && accountErrors.length === 0 && accountNumber.length === 7;
                        accountNumber &&
                        accountErrors.length === 0 &&
                        accountNumber.length > 0;

                      // Case 2: If Account Number is valid, this field is REQUIRED
                      if (isAccountValid) {
                        if (!value || value.length === 0) {
                          return Promise.reject(
                            new Error(
                              "Please select at least one test data option",
                            ),
                          );
                        }
                      }

                      // Case 1: If Account Number is empty (or invalid), this field is OPTIONAL
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                {testDataFileTypes ? (
                  <Checkbox.Group disabled={isTestDataDisabled}>
                    {testDataFileTypes.map((type) => (
                      <Checkbox key={type?.id} value={type?.id}>
                        {type?.name}
                      </Checkbox>
                    ))}
                  </Checkbox.Group>
                ) : (
                  <p className="disabled-label">
                    No test data avialble at this moment. Please contact
                    support.
                  </p>
                )}
              </Form.Item>
            </FormLineItemWrapper>
          </Form>
        )}
      </Modal>
    </>
  );
};

export default EditCustomerInfoModal;
